// Formatting helpers shared by the printable documents (mirror of the legacy
// formatNumber / formatQuantity / metaString mixins).

import type { StoreSettings } from './storeSettings';

export type AnyRecord = Record<string, unknown>;

export function toNumber(value: unknown): number {
  const number = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
  return Number.isFinite(number) ? number : 0;
}

function decimals(value: unknown, fallback: number) {
  const parsed = parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 6 ? parsed : fallback;
}

export function makeFormatters(settings: StoreSettings) {
  const moneyDigits = decimals(settings.decimals, 2);
  const qtyDigits = decimals(settings.quantity_decimals, 2);
  return {
    money: (value: unknown) => toNumber(value).toLocaleString(undefined, { minimumFractionDigits: moneyDigits, maximumFractionDigits: moneyDigits }),
    qty: (value: unknown) => toNumber(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: qtyDigits })
  };
}

export type Formatters = ReturnType<typeof makeFormatters>;

export function asRecord(value: unknown): AnyRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as AnyRecord) : null;
}

export function asArray(value: unknown): AnyRecord[] {
  return Array.isArray(value) ? value.filter((entry): entry is AnyRecord => Boolean(entry) && typeof entry === 'object') : [];
}

export function str(value: unknown): string {
  if (value == null) return '';
  return String(value);
}

// Variation meta ({ Color: 'Red', Size: 'M' }) → "Color: Red, Size: M".
export function metaString(meta: unknown): string {
  let source = meta;
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch {
      return source as string;
    }
  }
  const record = asRecord(source);
  if (!record) return '';
  return Object.entries(record)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
}

// Custom field values with their definitions (record.attributes from the API).
export function extraAttributeRows(record: AnyRecord): Array<{ label: string; value: string }> {
  const values = asRecord(record.extra_attributes);
  if (!values) return [];
  const definitions = asArray(record.attributes);
  return Object.entries(values)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([slug, value]) => {
      const definition = definitions.find((entry) => entry.slug === slug);
      return { label: str(definition?.name ?? slug), value: Array.isArray(value) ? value.join(', ') : str(value) };
    });
}

export function partyLines(party: AnyRecord | null): string[] {
  if (!party) return [];
  const address = [party.address, party.state_name, party.country_name].map(str).filter(Boolean).join(' ');
  return [address, str(party.phone), str(party.email)].filter(Boolean);
}

// Translated label for a payment gateway value, falling back to the raw value.
export function gatewayLabel(gateway: unknown, translate: (key: string) => string, has: (key: string) => boolean): string {
  const value = str(gateway);
  if (!value) return translate('orderActions.gateway.requesting');
  const key = `orderActions.gateway.${value}`;
  return has(key) ? translate(key) : value;
}
